import { Box } from '@mui/material'
import { useAppSelector } from 'hooks'
import { Country, MetricGroup, MetricValue } from 'types'
import { calculateCountryMetricWeight, generateNationalRankedChartData, getDataAgainstMetricKey, simplifyMetricGroup } from 'utils'
import { removePrefix } from 'utils/removePrefix'
import { PILLAR_INFO } from 'constant'
import { formatNumberChart, formatNumberDecimal, formatNumberPureDecimal } from 'utils/formatNumber'
import { useMemo } from 'react'
import { getCountryNameByCode } from 'utils/getCountryList'

interface IReportTable {
  countryName: string
  countryShortCode: string
  countryData: Country[]
  currentYear: number
  metricValue: MetricValue
  metricGroups: MetricGroup[]
}

const ReportTable: React.FC<IReportTable> = ({
  countryName,
  countryShortCode,
  countryData,
  currentYear,
  metricValue,
  metricGroups,
}) => {
  const metricDataTypeMap = useAppSelector(({ vibrancy }) => vibrancy.metricDataTypeMap)
  const vibrancyScore = useMemo(() => {
    return metricGroups?.reduce((acc: number, curr: any) => {
      const metricScore = calculateCountryMetricWeight(countryShortCode, countryData)
      const score = metricScore[curr.metricGroupNameDatakey]
      return (acc += !isNaN(Number(score)) ? score : 0)
    }, 0)
  }, [metricGroups])
  return (
    <>
      <TableReportHeadBanner />
      <table>
        <thead>
          {/* table column headers */}
          <tr>
            <th className='table-column-headers'>Individual Metrics</th>
            <th className='table-column-headers'>Value</th>
            <th className='table-column-headers'>Weight</th>
            <th className='table-column-headers'>Score</th>
            <th className='table-column-headers'>Rank</th>
          </tr>
        </thead>
        <tbody>
          {/* table metric */}
          {metricGroups.map((group) => {
            const simplifiedMetricGroup = simplifyMetricGroup(group, metricValue)
            const activeMetrics = simplifiedMetricGroup.map((metric) => {
              const data = getDataAgainstMetricKey(countryShortCode, countryData, {
                shortnameScaled: `${metric.metricShortNameScaled}_display` as string,
                shortNameRaw: metric.metricShortName as string,
                currentSelectedYear: currentYear,
                metricType: metricValue,
              })
              // const value = parseFloat(data.value?.toString() || '0') !== 0 ? data.value : '-'
              return { data, value: data.value, ...metric, multiplier: metric.multiplier }
            })
            // const invalidMetricsCount = activeMetrics.filter((metric) => metric.value === '-').length
            // const validMetricsCount = simplifiedMetricGroup.length - invalidMetricsCount

            // activeMetrics.forEach((metric) => {
            //   if (metric.value === '-') {
            //     metric.multiplier = 0
            //   } else {
            //     metric.multiplier = 1 / validMetricsCount
            //   }
            // })

            const metricScore = calculateCountryMetricWeight(countryShortCode, countryData)
            const score = metricScore[group.metricGroupNameDatakey]

            return (
              <div key={group.metricGroupName} className='table-border'>
                <tr className='metric-header'>
                  <td className='metric-name'>
                    {PILLAR_INFO.find((pillar) => pillar.name === group.metricGroupName)!.displayName}
                  </td>
                  <td className='metric-name'></td>
                  <td className='metric-weight'>{`${formatNumberDecimal(group.multiplier * 100)}`}%</td>
                  <td className='metric-score'>{formatNumberPureDecimal(score || 0, 2)}</td>
                  <td className='metric-name'></td>
                </tr>
                {activeMetrics.map((metric) => {
                  const cutMetricName = removePrefix(metric.metricName, 'Number of ')
                  let value: string | number = '-'
                  const isPercentage = metricDataTypeMap[metric.metricShortName] === 'Percentage'
                  if (metric?.data?.value?.toString() !== 'NaN' && !isPercentage) {
                    value = metric?.data?.value ? formatNumberChart(metric?.data?.value, 2) : '-*'
                  } else if (metric?.data?.value?.toString() !== 'NaN' && isPercentage) {
                    value = metric?.data?.value
                      ? formatNumberChart(metric?.data?.value * 100, 2) +
                        // (data.value * 100).toFixed(2)
                        '%'
                      : '-*'
                  }

                  if (metric?.value === 0) {
                    value = `0${isPercentage ? '%' : ''}`
                  }
                  const displayMultiplier = formatNumberDecimal(metric.multiplier * 100) + '%'
                  const dataRank = generateNationalRankedChartData(metricGroups, countryData, {
                    globalYear: currentYear,
                    metricName: metric.metricName,
                  })
                  const countryDataRank = dataRank?.find((el) => el.countryName === countryName)
                  const gRank = countryDataRank ? countryDataRank.cRank : 0
                  const ranking = `${gRank}/${dataRank.length}`
                  return (
                    <tr className='sub-metric-header' key={metric.metricName}>
                      <td className='sub-metric-name'>{cutMetricName}</td>
                      <td className=''>{value}</td>
                      <td className=''>
                        {Number(metric.multiplier) || Number(metric.multiplier) === 0 ? displayMultiplier : '-'}
                      </td>{' '}
                      {/* weight column */}
                      <td className=''>
                        {metric?.data?.score || metric?.data?.score === 0
                          ? formatNumberPureDecimal(metric.data.score, 2)
                          : '-*'}
                      </td>
                      <td className=''>{ranking}</td>
                    </tr>
                  )
                })}
              </div>
            )
          })}
          <div key={'table-footer'} className='table-border'>
            <tr className='metric-header'>
              <td className='metric-name no-border'>Vibrancy Score</td>
              <td className='metric-name no-border'></td>
              <td className='metric-weight no-border'></td>
              <td className='metric-score no-border'>{formatNumberDecimal(vibrancyScore, 2)}</td>
              <td className='metric-name no-border'></td>
            </tr>
          </div>
        </tbody>
      </table>
    </>
  )
}

function TableReportHeadBanner() {
  const { yearFilter } = useAppSelector(({ vibrancy }) => vibrancy.configuration)
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#c2ddee',
        borderRadius: '14px',
        padding: '14px 25px',
        width: '100%', // Assuming the banner should be full width
      }}
    >
      <span style={{ fontFamily: 'CircularStdMedium' }}>AI Vibrancy Indicators</span>
      <span style={{ fontFamily: 'CircularStdMedium' }}>{yearFilter.currentYear}</span>
    </Box>
  )
}

export default ReportTable
